import { lazy } from 'react';
import { createBrowserRouter, RouteObject } from 'react-router-dom';

import { AuthKitWrapper } from '@/auth/AuthKitWrapper';
import { RoutesEnum } from '@/enums';

import { LeaderInsightsGuard } from './components/LeaderInsightsGuard';
import { RoleGuard } from './components/RoleGuard';
import { Route } from './components/Route';

const ErrorPage = lazy(() =>
  import('../pages/Error').then(({ ErrorPage }) => ({ default: ErrorPage }))
);
const NotFoundPage = lazy(() =>
  import('../pages/NotFound').then(({ NotFoundPage }) => ({ default: NotFoundPage }))
);
const LoginPage = lazy(() =>
  import('../pages/Auth/Login').then(({ Login }) => ({ default: Login }))
);
const OnboardingPage = lazy(() =>
  import('../pages/Auth/Onboarding').then(({ Onboarding }) => ({ default: Onboarding }))
);
const IntegrationsSetupWorkplacePage = lazy(() =>
  import('../pages/Integrations/IntegrationsSetupWorkplace').then(
    ({ IntegrationsSetupWorkplace }) => ({ default: IntegrationsSetupWorkplace })
  )
);
const IntegrationsSetupSSOPage = lazy(() =>
  import('../pages/Integrations/IntegrationsSetupSSO').then(({ IntegrationsSetupSSO }) => ({
    default: IntegrationsSetupSSO,
  }))
);
const IntegrationsSetupOrgChatPage = lazy(() =>
  import('../pages/Integrations/IntegrationsSetupOrgChat').then(({ IntegrationsSetupOrgChat }) => ({
    default: IntegrationsSetupOrgChat,
  }))
);
const IntegrationsSetupCommunicationToolPage = lazy(() =>
  import('../pages/Integrations/IntegrationsSetupCommunicationTool').then(
    ({ IntegrationsSetupCommunicationTool }) => ({ default: IntegrationsSetupCommunicationTool })
  )
);
const GetStartedGate = lazy(() =>
  import('../pages/GetStarted/GetStartedGate').then(({ GetStartedGate }) => ({
    default: GetStartedGate,
  }))
);
const GetStartedBillingPage = lazy(() =>
  import('../pages/GetStarted/Billing').then(({ GetStartedBillingPage }) => ({
    default: GetStartedBillingPage,
  }))
);
const GetStartedBillingCompletePage = lazy(() =>
  import('../pages/GetStarted/BillingComplete').then(({ GetStartedBillingCompletePage }) => ({
    default: GetStartedBillingCompletePage,
  }))
);
const DailySurveyOverviewPage = lazy(() =>
  import('../pages/DailySurvey/DailySurveyOverview').then(({ DailySurveyOverviewPage }) => ({
    default: DailySurveyOverviewPage,
  }))
);
const QuestionHistoryPage = lazy(() =>
  import('../pages/DailySurvey/QuestionHistory').then(({ QuestionHistoryPage }) => ({
    default: QuestionHistoryPage,
  }))
);
const QuestionHistoryDetailsPage = lazy(() =>
  import('../pages/DailySurvey/QuestionHistoryDetails').then(({ QuestionHistoryDetailsPage }) => ({
    default: QuestionHistoryDetailsPage,
  }))
);
const QuestionHistoryEnpsDetailsPage = lazy(() =>
  import('../pages/DailySurvey/QuestionHistoryEnpsDetails').then(
    ({ QuestionHistoryEnpsDetailsPage }) => ({
      default: QuestionHistoryEnpsDetailsPage,
    })
  )
);
const LeaderInsightsPage = lazy(() =>
  import('../pages/DailySurvey/LeaderInsights').then(({ LeaderInsightsPage }) => ({
    default: LeaderInsightsPage,
  }))
);
const LeaderInsightsDetailsPage = lazy(() =>
  import('../pages/DailySurvey/LeaderInsightsDetails').then(({ LeaderInsightsDetailsPage }) => ({
    default: LeaderInsightsDetailsPage,
  }))
);
const DataExplorerPage = lazy(() =>
  import('../pages/DailySurvey/DataExplorer').then(({ DataExplorerPage }) => ({
    default: DataExplorerPage,
  }))
);
const DataExplorerBuilderPage = lazy(() =>
  import('../pages/DailySurvey/DataExplorerBuilder').then(({ DataExplorerBuilderPage }) => ({
    default: DataExplorerBuilderPage,
  }))
);
const DataExplorerDetailsPage = lazy(() =>
  import('../pages/DailySurvey/DataExplorerDetails').then(({ DataExplorerDetailsPage }) => ({
    default: DataExplorerDetailsPage,
  }))
);
const QuestionLibraryPage = lazy(() =>
  import('../pages/DailySurvey/Management/QuestionLibrary').then(({ QuestionLibraryPage }) => ({
    default: QuestionLibraryPage,
  }))
);
const QuestionLibraryDetails = lazy(() =>
  import('../pages/DailySurvey/Management/QuestionLibraryDetails').then(
    ({ QuestionLibraryDetailsPage }) => ({
      default: QuestionLibraryDetailsPage,
    })
  )
);
const QuestionSchedulePage = lazy(() =>
  import('../pages/DailySurvey/Management/QuestionSchedule').then(({ QuestionSchedulePage }) => ({
    default: QuestionSchedulePage,
  }))
);
const SurveysOverviewPage = lazy(() =>
  import('../pages/Surveys/SurveysOverview').then(({ SurveysOverviewPage }) => ({
    default: SurveysOverviewPage,
  }))
);
const SurveysOverviewDetailsPage = lazy(() =>
  import('../pages/Surveys/SurveysOverviewDetails').then(({ SurveysOverviewDetails }) => ({
    default: SurveysOverviewDetails,
  }))
);
const SurveysOverviewBuilderPage = lazy(() =>
  import('../pages/Surveys/SurveysOverviewBuilder').then(({ SurveysOverviewBuilder }) => ({
    default: SurveysOverviewBuilder,
  }))
);
const SurveysOverviewTemplatesPage = lazy(() =>
  import('../pages/Surveys/SurveysOverviewTemplates').then(({ SurveysOverviewTemplates }) => ({
    default: SurveysOverviewTemplates,
  }))
);
const SurveysTemplatesPage = lazy(() =>
  import('../pages/Surveys/SurveysTemplates').then(({ SurveysTemplatesPage }) => ({
    default: SurveysTemplatesPage,
  }))
);
const SettingsPage = lazy(() =>
  import('../pages/Settings').then(({ SettingsPage }) => ({
    default: SettingsPage,
  }))
);
const HelpPage = lazy(() =>
  import('../pages/Help').then(({ HelpPage }) => ({
    default: HelpPage,
  }))
);
const AccountSettingsPage = lazy(() =>
  import('../pages/AccountSettings').then(({ AccountSettingsPage }) => ({
    default: AccountSettingsPage,
  }))
);

const withSidebarRoutes: RouteObject[] = [
  {
    path: RoutesEnum.GET_STARTED,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={GetStartedGate} />,
  },
  {
    path: RoutesEnum.GET_STARTED_BILLING,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={GetStartedBillingPage} />,
  },
  {
    path: RoutesEnum.GET_STARTED_BILLING_COMPLETE,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={GetStartedBillingCompletePage} />,
  },
  {
    path: RoutesEnum.GET_STARTED_SSO,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={IntegrationsSetupSSOPage} />,
  },
  {
    path: RoutesEnum.GET_STARTED_ORG_CHART,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={IntegrationsSetupOrgChatPage} />,
  },
  {
    path: RoutesEnum.GET_STARTED_COMMUNICATION_TOOL,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={IntegrationsSetupCommunicationToolPage} />,
  },
  {
    path: RoutesEnum.DAILY_SURVEY_OVERVIEW,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={DailySurveyOverviewPage} />,
  },
  {
    path: RoutesEnum.QUESTION_HiSTORY,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={QuestionHistoryPage} />,
  },
  {
    path: RoutesEnum.QUESTION_HISTORY_ENPS_DETAILS,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={QuestionHistoryEnpsDetailsPage} />,
  },
  {
    path: RoutesEnum.QUESTION_HiSTORY_DETAILS,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={QuestionHistoryDetailsPage} />,
  },
  {
    path: RoutesEnum.LEADER_INSIGHTS,
    errorElement: <Route component={ErrorPage} />,
    element: (
      <Route
        component={() => (
          <LeaderInsightsGuard>
            <LeaderInsightsPage />
          </LeaderInsightsGuard>
        )}
      />
    ),
  },
  {
    path: RoutesEnum.LEADER_INSIGHTS_DETAILS,
    errorElement: <Route component={ErrorPage} />,
    element: (
      <Route
        component={() => (
          <LeaderInsightsGuard>
            <LeaderInsightsDetailsPage />
          </LeaderInsightsGuard>
        )}
      />
    ),
  },
  {
    path: RoutesEnum.DATA_EXPLORER,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={DataExplorerPage} />,
  },
  {
    path: RoutesEnum.DATA_EXPLORER_BUILDER,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={DataExplorerBuilderPage} />,
  },
  {
    path: RoutesEnum.DATA_EXPLORER_DETAILS,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={DataExplorerDetailsPage} />,
  },
  {
    path: RoutesEnum.MANAGEMENT_QUESTION_LIBRARY,
    errorElement: <Route component={ErrorPage} />,
    element: (
      <Route
        component={() => (
          <RoleGuard allowedRoles={['admin', 'people_experience']}>
            <QuestionLibraryPage />
          </RoleGuard>
        )}
      />
    ),
  },
  {
    path: RoutesEnum.MANAGEMENT_QUESTION_LIBRARY_DETAILS,
    errorElement: <Route component={ErrorPage} />,
    element: (
      <Route
        component={() => (
          <RoleGuard allowedRoles={['admin', 'people_experience']}>
            <QuestionLibraryDetails />
          </RoleGuard>
        )}
      />
    ),
  },
  {
    path: RoutesEnum.MANAGEMENT_QUESTION_SCHEDULE,
    errorElement: <Route component={ErrorPage} />,
    element: (
      <Route
        component={() => (
          <RoleGuard allowedRoles={['admin', 'people_experience']}>
            <QuestionSchedulePage />
          </RoleGuard>
        )}
      />
    ),
  },
  {
    path: RoutesEnum.SURVEYS_OVERVIEW,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={SurveysOverviewPage} />,
  },
  {
    path: RoutesEnum.SURVEYS_OVERVIEW_DETAILS,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={SurveysOverviewDetailsPage} />,
  },
  {
    path: RoutesEnum.SURVEYS_OVERVIEW_TEMPLATES,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={SurveysOverviewTemplatesPage} />,
  },
  {
    path: RoutesEnum.SURVEYS_TEMPLATES,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={SurveysTemplatesPage} />,
  },
  {
    path: RoutesEnum.SETTINGS,
    errorElement: <Route component={ErrorPage} />,
    element: (
      <Route
        component={() => (
          <RoleGuard allowedRoles={['admin']}>
            <SettingsPage />
          </RoleGuard>
        )}
      />
    ),
  },
  {
    path: RoutesEnum.HELP,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={HelpPage} />,
  },
  {
    path: RoutesEnum.ACCOUNT_SETTINGS,
    errorElement: <Route component={ErrorPage} />,
    element: <Route component={AccountSettingsPage} />,
  },
  {
    path: '*',
    element: <Route component={NotFoundPage} />,
  },
];

const withoutSidebarRoutes: RouteObject[] = [
  {
    path: RoutesEnum.LOGIN,
    errorElement: <Route component={ErrorPage} withoutSidebar />,
    element: <Route component={LoginPage} withoutSidebar />,
  },
  {
    path: RoutesEnum.ONBOARDING,
    errorElement: <Route component={ErrorPage} withoutSidebar />,
    element: <Route component={OnboardingPage} withoutSidebar />,
  },
  {
    path: RoutesEnum.INTEGRATIONS,
    errorElement: <Route component={ErrorPage} withoutSidebar />,
    element: <Route component={IntegrationsSetupWorkplacePage} withoutSidebar />,
  },
  {
    path: RoutesEnum.INTEGRATIONS_SSO,
    errorElement: <Route component={ErrorPage} withoutSidebar />,
    element: <Route component={IntegrationsSetupSSOPage} withoutSidebar />,
  },
  {
    path: RoutesEnum.INTEGRATIONS_ORG_CHAT,
    errorElement: <Route component={ErrorPage} withoutSidebar />,
    element: <Route component={IntegrationsSetupOrgChatPage} withoutSidebar />,
  },
  {
    path: RoutesEnum.INTEGRATIONS_COMMUNICATION_TOOL,
    errorElement: <Route component={ErrorPage} withoutSidebar />,
    element: <Route component={IntegrationsSetupCommunicationToolPage} withoutSidebar />,
  },
  {
    path: RoutesEnum.SURVEYS_OVERVIEW_BUILDER,
    errorElement: <Route component={ErrorPage} withoutSidebar />,
    element: <Route component={SurveysOverviewBuilderPage} withoutSidebar />,
  },
];

const baseRoutes: RouteObject[] = [
  {
    path: RoutesEnum.GET_STARTED,
    errorElement: <Route component={ErrorPage} />,
    element: <AuthKitWrapper />,
    children: [...withSidebarRoutes, ...withoutSidebarRoutes],
  },
];

export const router = createBrowserRouter(baseRoutes);
